/**
 * Seeded random numbers (mulberry32). The whole generator state is one uint32 stored in the world state,
 * so a game replays exactly from its seed. Never use Math.random in the engine.
 */
export class Rng {
  state: number;

  constructor(state: number) {
    this.state = state >>> 0;
  }

  /** Uniform float in [0, 1). */
  float(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Integer in [0, n). */
  int(n: number): number {
    return Math.floor(this.float() * n);
  }

  /** A ten-sided die: 1..10. */
  d10(): number {
    return this.int(10) + 1;
  }

  /** True with the given percent chance (0-100). */
  chance(percent: number): boolean {
    return this.float() * 100 < percent;
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('Rng.pick on an empty list');
    return items[this.int(items.length)] as T;
  }

  /** Fisher-Yates shuffle into a new array. */
  shuffle<T>(items: readonly T[]): T[] {
    const out = items.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = this.int(i + 1);
      const a = out[i] as T;
      out[i] = out[j] as T;
      out[j] = a;
    }
    return out;
  }
}

/** Mix a seed and a label into a new uint32 seed (FNV-1a), for independent streams. */
export function deriveSeed(seed: number, label: string): number {
  let h = 0x811c9dc5 ^ (seed >>> 0);
  for (let i = 0; i < label.length; i++) {
    h ^= label.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}
